import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, Users, Package, Building2, Truck, Loader2, Lock, CheckCircle2, Trash2, AlertTriangle, X, Send, Bell, ChevronRight } from "lucide-react";
import { adminFetch } from "@/lib/api";
import { Loader, Card, EmptyState } from "@/components/common";
import { cn } from "@/lib/utils";
import AdminServices from "@/pages/admin/AdminServices";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";

const TABS = [
  ["dashboard", "Dashboard"],
  ["aziende", "Aziende"],
  ["vettori", "Vettori"],
  ["autisti", "Autisti"],
  ["trips", "Viaggi"],
  ["missions", "Missioni"],
  ["notifiche", "Notifiche"],
  ["documenti", "Documenti"],
  ["servizi", "Borsa Servizi"],
  ["feedback", "Segnalazioni"],
  ["audit", "Audit log"],
  ["sicurezza", "Sicurezza"],
];
const ROLE_OF_TAB = { aziende: "azienda", vettori: "vettore", autisti: "autista" };
const SEARCHABLE = new Set(["aziende", "vettori", "autisti", "trips", "missions", "notifiche"]);
// Account super-protetto: mai eliminabile dal pannello Admin (coerente col blocco 403 lato backend)
const PROTECTED_DELETE_EMAIL = "mauriziodigiovanni@me.com";
const FEEDBACK_STATUSES = { nuovo: "Nuovo", in_valutazione: "In valutazione", pianificato: "Pianificato", risolto: "Risolto", archiviato: "Archiviato" };

function AdminSecurity() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErrorText("");
    if (!current) { setErrorText("Inserisci la password attuale."); return; }
    if (next.length < 8) { setErrorText("La nuova password deve avere almeno 8 caratteri."); return; }
    if (next !== confirm) { setErrorText("Le nuove password non coincidono."); return; }
    setLoading(true);
    try {
      await adminFetch("/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: current, new_password: next }) });
      setDone(true); setCurrent(""); setNext(""); setConfirm("");
    } catch (error) {
      setErrorText(error.message || "Operazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const inputWrap = "flex min-h-[48px] items-center gap-2.5 rounded-xl border border-border bg-card px-3";

  return (
    <Card className="max-w-lg space-y-3">
      <div className="flex items-center gap-2">
        <Lock className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-extrabold text-foreground">Cambia password</h2>
      </div>
      <p className="text-[13px] text-muted-foreground">
        Aggiorna la password del tuo account amministratore. Verrà verificata la password attuale; email, ruolo e permessi restano invariati.
      </p>
      {done ? (
        <div data-testid="admin-password-success" className="flex items-center gap-2 rounded-xl bg-[hsl(var(--success))]/10 px-3 py-3 text-[13px] font-semibold text-[hsl(var(--success))]">
          <CheckCircle2 className="h-5 w-5" /> Password aggiornata con successo.
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="mb-1 block text-[13px] font-bold text-card-foreground">Password attuale</label>
          <div className={inputWrap}>
            <Lock className="h-4 w-4 text-muted-foreground" />
            <input data-testid="admin-change-current" type="password" placeholder="Password attuale" value={current} onChange={(e) => { setCurrent(e.target.value); setDone(false); }} className="h-11 flex-1 bg-transparent text-[15px] text-foreground outline-none" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[13px] font-bold text-card-foreground">Nuova password</label>
          <div className={inputWrap}>
            <Lock className="h-4 w-4 text-muted-foreground" />
            <input data-testid="admin-change-new" type="password" placeholder="Minimo 8 caratteri" value={next} onChange={(e) => { setNext(e.target.value); setDone(false); }} className="h-11 flex-1 bg-transparent text-[15px] text-foreground outline-none" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[13px] font-bold text-card-foreground">Conferma nuova password</label>
          <div className={inputWrap}>
            <Lock className="h-4 w-4 text-muted-foreground" />
            <input data-testid="admin-change-confirm" type="password" placeholder="Ripeti la nuova password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setDone(false); }} className="h-11 flex-1 bg-transparent text-[15px] text-foreground outline-none" />
          </div>
        </div>
        {errorText ? <p data-testid="admin-change-error" className="text-[13px] font-semibold text-destructive">{errorText}</p> : null}
        <button type="submit" data-testid="admin-change-submit" disabled={loading} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground hover:opacity-90 disabled:opacity-60">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {loading ? "Aggiornamento..." : "AGGIORNA PASSWORD"}
        </button>
      </form>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
        <Icon className="h-5 w-5 text-primary" />
      </span>
      <div>
        <p className="text-2xl font-extrabold text-foreground">{value ?? "—"}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [missions, setMissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [impact, setImpact] = useState(null);
  const [cascade, setCascade] = useState(false);

  useEffect(() => {
    if (!deleteTarget) { setImpact(null); setCascade(false); return; }
    setImpact(null);
    adminFetch(`/admin/users/${deleteTarget.id}/deletion-impact`).then(setImpact).catch(() => setImpact({ trips: 0, missions: 0 }));
  }, [deleteTarget]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "dashboard") setStats(await adminFetch("/admin/stats"));
      else if (ROLE_OF_TAB[tab]) setUsers((await adminFetch(`/admin/users?role=${ROLE_OF_TAB[tab]}&q=${encodeURIComponent(q)}`)).items || []);
      else if (tab === "trips") setTrips((await adminFetch(`/admin/trips?q=${encodeURIComponent(q)}`)).items || []);
      else if (tab === "missions") setMissions((await adminFetch(`/admin/missions?q=${encodeURIComponent(q)}`)).items || []);
      else if (tab === "notifiche") setNotifications((await adminFetch(`/admin/notifications?q=${encodeURIComponent(q)}`)).items || []);
      else if (tab === "feedback") setFeedback(await adminFetch("/admin/feedback"));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [tab, q]);

  useEffect(() => {
    if (selectedUser) return undefined;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, selectedUser]);

  const changeTab = (k) => { setSelectedUser(null); setQ(""); setTab(k); };

  const toggleSuspend = async (u) => {
    const path = u.suspended ? "reactivate" : "suspend";
    try {
      await adminFetch(`/admin/users/${u.id}/${path}`, { method: "POST", body: "{}" });
      setUsers((old) => old.map((it) => (it.id === u.id ? { ...it, suspended: !u.suspended } : it)));
    } catch {
      /* ignore */
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await adminFetch(`/admin/users/${deleteTarget.id}?cascade=${cascade}`, { method: "DELETE" });
      setUsers((old) => old.filter((it) => it.id !== deleteTarget.id));
      setDeleteTarget(null);
      try { setStats(await adminFetch("/admin/stats")); } catch { /* ignore */ }
    } catch (error) {
      setDeleteError(error.message || "Eliminazione non riuscita. Riprova.");
    } finally {
      setDeleting(false);
    }
  };

  const setFeedbackStatus = async (ticket, status) => {
    try {
      await adminFetch(`/admin/feedback/${ticket.id}/status`, { method: "POST", body: JSON.stringify({ status }) });
      setFeedback((old) => old.map((it) => (it.id === ticket.id ? { ...it, status } : it)));
    } catch {
      /* ignore */
    }
  };

  const renderUserCard = (u) => (
    <Card key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3">
      <button onClick={() => setSelectedUser(u.id)} data-testid={`admin-user-open-${u.id}`} className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <div className="min-w-0 flex-1">
          <p className="truncate font-extrabold text-foreground">{u.company_name}</p>
          <p className="truncate text-xs text-muted-foreground">{u.email} · {u.account_type}{u.vat_number ? ` · ${u.vat_number}` : ""}</p>
          {u.suspended ? <span className="text-[11px] font-extrabold text-destructive">Sospeso</span> : null}
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      <button data-testid={`admin-user-toggle-${u.id}`} onClick={() => toggleSuspend(u)} className={cn("shrink-0 rounded-lg border px-3 py-2 text-xs font-extrabold", u.suspended ? "border-primary text-primary" : "border-destructive text-destructive")}>
        {u.suspended ? "Riattiva" : "Sospendi"}
      </button>
      {(u.email || "").toLowerCase() === PROTECTED_DELETE_EMAIL ? (
        <span data-testid={`admin-user-protected-${u.id}`} className="flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-bold text-muted-foreground" title="Account protetto: non eliminabile">
          <Lock className="h-3.5 w-3.5" /> Protetto
        </span>
      ) : (
        <button data-testid={`admin-user-delete-${u.id}`} onClick={() => { setDeleteError(""); setDeleteTarget(u); }} className="flex shrink-0 items-center gap-1 rounded-lg border border-destructive bg-destructive/10 px-3 py-2 text-xs font-extrabold text-destructive hover:bg-destructive/20">
          <Trash2 className="h-3.5 w-3.5" /> Elimina
        </button>
      )}
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-extrabold text-foreground">Pannello Admin</h1>
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => changeTab(k)} className={cn("shrink-0 rounded-full px-4 py-2 text-xs font-bold", tab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>
            {l}
          </button>
        ))}
      </div>

      {selectedUser ? (
        <AdminUserDetail userId={selectedUser} onBack={() => setSelectedUser(null)} />
      ) : (
        <>
          {SEARCHABLE.has(tab) ? (
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="min-h-[46px] w-full rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary" data-testid="admin-search" />
          ) : null}

          {tab === "servizi" ? (
            <AdminServices />
          ) : tab === "sicurezza" ? (
            <AdminSecurity />
          ) : tab === "documenti" ? (
            <AdminDocuments />
          ) : tab === "audit" ? (
            <AdminAuditLog />
          ) : loading ? (
            <Loader />
          ) : tab === "dashboard" ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatCard icon={Users} label="Utenti totali" value={stats?.users_total} />
              <StatCard icon={Building2} label="Aziende" value={stats?.aziende} />
              <StatCard icon={Truck} label="Vettori" value={stats?.vettori} />
              <StatCard icon={Users} label="Autisti" value={stats?.autisti} />
              <StatCard icon={Package} label="Viaggi totali" value={stats?.trips_total} />
              <StatCard icon={Package} label="Viaggi attivi" value={stats?.trips_active} />
              <StatCard icon={Package} label="Completati" value={stats?.trips_completed} />
              <StatCard icon={Truck} label="Mezzi flotta" value={stats?.fleet_total} />
              <StatCard icon={Building2} label="Fornitori" value={stats?.providers_total} />
            </div>
          ) : ROLE_OF_TAB[tab] ? (
            users.length === 0 ? <EmptyState icon={Users} title="Nessun utente" /> : users.map(renderUserCard)
          ) : tab === "trips" ? (
            trips.length === 0 ? <EmptyState icon={Package} title="Nessun viaggio" /> : trips.map((t) => (
              <Link key={t.id} to={`/trip/${t.id}`} data-testid={`admin-trip-${t.id}`}>
                <Card className="hover:shadow-md">
                  <p className="truncate font-extrabold text-foreground">{t.pickup} → {t.delivery}</p>
                  <p className="text-xs text-muted-foreground">{t.date} · {t.owner_name} · {t.status_label || t.status}</p>
                </Card>
              </Link>
            ))
          ) : tab === "missions" ? (
            missions.length === 0 ? <EmptyState icon={Send} title="Nessuna missione" /> : missions.map((m) => (
              <Card key={m.id} data-testid={`admin-mission-${m.id}`}>
                <p className="truncate font-extrabold text-foreground">{m.pickup_address} → {m.delivery_address}</p>
                <p className="text-xs text-muted-foreground">{m.date} · {m.driver_name || "—"} · {m.carrier?.name || ""} · {m.status}</p>
              </Card>
            ))
          ) : tab === "notifiche" ? (
            notifications.length === 0 ? <EmptyState icon={Bell} title="Nessuna notifica" /> : notifications.map((n) => (
              <Card key={n.id} data-testid={`admin-notification-${n.id}`}>
                <p className="truncate font-extrabold text-foreground">{n.title}</p>
                <p className="truncate text-xs text-muted-foreground">{n.message} · {n.recipient?.name || ""} · {String(n.created_at || "").slice(0, 10)}</p>
              </Card>
            ))
          ) : feedback.length === 0 ? (
            <EmptyState icon={Shield} title="Nessuna segnalazione" />
          ) : (
            feedback.map((t) => (
              <Card key={t.id} data-testid={`admin-feedback-${t.id}`} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-extrabold text-foreground">{t.title || t.category}</p>
                    <p className="text-xs text-muted-foreground">{t.user_name} · {t.user_role}</p>
                  </div>
                  <select data-testid={`admin-feedback-status-${t.id}`} value={t.status} onChange={(e) => setFeedbackStatus(t, e.target.value)} className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none">
                    {Object.entries(FEEDBACK_STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                {t.last_message ? <p className="text-[13px] text-card-foreground">{t.last_message}</p> : null}
              </Card>
            ))
          )}
        </>
      )}

      {deleteTarget ? (
        <div data-testid="admin-delete-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-lg font-extrabold text-foreground">Elimina utente</p>
                <p className="text-[13px] text-muted-foreground">Stai per eliminare definitivamente questo account.</p>
              </div>
              <button data-testid="admin-delete-close" onClick={() => setDeleteTarget(null)} disabled={deleting} className="rounded-lg p-1 text-muted-foreground hover:bg-secondary">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-1 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm">
              <p><span className="font-bold text-card-foreground">Nome:</span> <span className="text-muted-foreground">{deleteTarget.company_name}</span></p>
              <p><span className="font-bold text-card-foreground">Email:</span> <span className="text-muted-foreground">{deleteTarget.email}</span></p>
              <p><span className="font-bold text-card-foreground">Ruolo:</span> <span className="text-muted-foreground">{deleteTarget.account_type}</span></p>
            </div>

            <div className="mt-3 flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-[13px] font-semibold text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Questa operazione è <strong>definitiva e irreversibile</strong>. Verranno rimossi anche i dati collegati appartenenti esclusivamente a questo utente. L&apos;utente non potrà più accedere.</span>
            </div>

            {impact && (impact.trips > 0 || impact.missions > 0) ? (
              <label data-testid="admin-delete-cascade" className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
                <input data-testid="admin-delete-cascade-checkbox" type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--destructive))]" />
                <span className="text-[13px] text-card-foreground">
                  <span className="font-extrabold">Elimina anche i viaggi e le missioni collegati</span>
                  <br />
                  <span className="text-muted-foreground">Verranno eliminati definitivamente <strong>{impact.trips} viaggi</strong> e <strong>{impact.missions} missioni</strong> appartenenti esclusivamente a questo utente (con allegati, chat, eventi e notifiche non condivisi). I dati condivisi con altri utenti restano e vengono anonimizzati.</span>
                </span>
              </label>
            ) : null}

            {deleteError ? <p data-testid="admin-delete-error" className="mt-2 text-[13px] font-semibold text-destructive">{deleteError}</p> : null}

            <div className="mt-4 flex gap-2">
              <button data-testid="admin-delete-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 rounded-xl border border-border px-4 py-3 text-sm font-extrabold text-card-foreground hover:bg-secondary disabled:opacity-60">
                Annulla
              </button>
              <button data-testid="admin-delete-confirm" onClick={confirmDelete} disabled={deleting} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-destructive px-4 py-3 text-sm font-extrabold text-destructive-foreground hover:opacity-90 disabled:opacity-60">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? "Eliminazione..." : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
